import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Button, Title } from '@mantine/core';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { htmlLegendPlugin } from 'shared/chartjs/htmlLegend';

interface ChartSectionProps {
  title: string;
  data: any;
  getDataSet: () => any[];
  translations: any;
  legendId: string;
}

export const ChartSection: React.FC<ChartSectionProps> = ({
  title,
  data,
  getDataSet,
  translations,
  legendId,
}) => {
  const handle = useFullScreenHandle();

  const datasets = useMemo(() => getDataSet(), [getDataSet]);
  const labels = useMemo(() => {
    // Extract all unique dateIds with their order from the datasets
    const dateMap = new Map<string, number>();

    datasets.forEach(dataset => {
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((point: any, index: number) => {
          if (point.x) {
            // Use index as order if order not explicitly provided
            const order = point.order !== undefined ? point.order : index;
            const existingOrder = dateMap.get(point.x);
            if (!existingOrder || existingOrder > order) {
              dateMap.set(point.x, order);
            }
          }
        });
      }
    });

    // Sort by order and return dateIds
    return Array.from(dateMap.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([dateId]) => dateId);
  }, [datasets]);

  return (
    <div className="col-md-4">
      <FullScreen handle={handle} className={'Fullscreen'}>
        <div className="Fullscreen__title">
          <Title order={4}>{title}</Title>
          <Button
            p={4}
            onClick={!handle.active ? handle.enter : handle.exit}
            size="small"
            variant="outline">
            {!handle.active && <i className="fas fa-expand" />}
            {handle.active && <i className="fas fa-compress" />}
          </Button>
        </div>
        <div style={{ maxHeight: 800 }}>
          <div id={legendId} />
          <Line
            data={{
              labels: labels,
              datasets: datasets,
            }}
            options={{
              pointRadius: 5,
              maintainAspectRatio: true,
              responsive: true,
              scales: {
                xAxis: {
                  title: {
                    display: true,
                    text: translations.global.inspectionDate,
                    font: { size: 14, weight: 'bold' },
                  },
                },
                yAxis: {
                  title: {
                    display: true,
                    text: translations.global.amount,
                    font: { size: 14, weight: 'bold' },
                  },
                  ticks: { stepSize: 10 },
                },
              },
              plugins: {
                // @ts-ignore
                htmlLegend: { containerID: legendId },
                legend: { display: false },
              },
            }}
            plugins={[htmlLegendPlugin]}
          />
        </div>
      </FullScreen>
    </div>
  );
};
